import React from "react";
import { createUseStyles } from "react-jss";
import PropTypes from "prop-types";
import _ from "lodash";
import jssVariables from "../../../jss_setup";
import { useContext } from "react";
import { CurrencyContext, StyleTypeContext, TranslationContext } from "../../../umbilical_tools";
import { Form, Formik } from "formik";
import { DatepickerField, ReactSelectField } from "../../forms/inputGroup/helpers";
import { DateTime } from "luxon";
import { useDispatch, useSelector } from "react-redux";
import { useState } from "react";
import { PaxPicker } from "../widgets";
import { NoScrollContext } from "../../../context";
import { useCallback } from "react";
import {
  ferryFiltersReset,
  ferrySearchFormReset,
  ferrySearchResultsReset,
} from "../../../actions/search/ferries_search";
import { PortAutoCompleteField } from "../../forms/inputGroup/helpers/PortAutoComplete";
import { getRoutesByOriginPort } from "../../../api/ferries";
import { validateFerrySearch } from "./helpers";
import { useEffect } from "react";
import { ferrySelectedReset } from "../../../actions/search/ferries_search";
import { useFerriesInitSearch } from "../../../hooks/ferries";
import { SearchButton } from "../../common/Button";

export function getIsRoundTripOpts(t) {
  return [
    { label: t("oneWay"), value: false },
    { label: t("roundTrip"), value: true },
  ];
}
const styles = createUseStyles({
  FerrySearchForm: (props) => ({
    padding: [0, jssVariables.normal_gap],
    ..._.get(props, "customStyles.FerrySearchForm", {}),
  }),
  form: (props) => ({
    display: "grid",
    ..._.get(props, "customStyles.form", {}),
  }),
  formContr: (props) => ({
    display: "grid",
    ..._.get(props, "customStyles.formContr", {}),
  }),
  row1: (props) => ({
    display: "grid",
    gridGap: jssVariables.half_gap,
    ..._.get(props, "customStyles.row1", {}),
  }),
  row2: (props) => ({
    display: "grid",
    gridGap: jssVariables.half_gap,
    ..._.get(props, "customStyles.row2", {}),
  }),
  mobileToggle: (props) => ({
    ..._.get(props, "customStyles.mobileToggle", {}),
  }),
  searchBtnContr: (props) => ({
    justifySelf: "center",
    paddingTop: jssVariables.normal_gap,
    ..._.get(props, "customStyles.searchBtnContr", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    FerrySearchForm: (props) => ({
      display: "grid",
      gridTemplateColumns: "1fr max-content",
      padding: 0,
      ..._.get(props, "customStyles.FerrySearchForm.smallscreen", {}),
    }),
    form: (props) => ({
      display: "grid",
      gridTemplateColumns: "1fr max-content",
      gridGap: jssVariables.half_gap,
      ..._.get(props, "customStyles.form.smallscreen", {}),
    }),
    formContr: (props) => ({
      gridTemplateColumns: "1fr",
      gridGap: jssVariables.half_gap,
      ..._.get(props, "customStyles.formContr.smallscreen", {}),
    }),
    row1: (props) => ({
      gridTemplateColumns: "10rem 1fr 1fr",
      ..._.get(props, "customStyles.row1.smallscreen", {}),
    }),
    row2: (props) => ({
      gridTemplateColumns: props.roundTrip ? "10rem 1fr 1fr 1fr" : "10rem 1fr 1fr",
      ..._.get(props, "customStyles.row2.smallscreen", {}),
    }),
    mobileToggle: (props) => ({
      ..._.get(props, "customStyles.mobileToggle.smallscreen", {}),
    }),
    searchBtnContr: (props) => ({
      padding: 0,
      alignSelf: "end",
      ..._.get(props, "customStyles.searchBtnContr.smallscreen", {}),
    }),
  },
  [`@media ${jssVariables.media.bigscreen}`]: {},
});
export const FerrySearchForm = ({ setShowPaxPickerModal }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [destPortsOptions, setDestPortsOptions] = useState([]);
  const [initSearch] = useFerriesInitSearch({
    setLoadingSearchBtn: setIsSubmitting,
  });
  const { type, brand, cStyler } = useContext(StyleTypeContext);
  const { t, i18n } = useContext(TranslationContext) as {
    t: (v: string) => string;
    i18n: { language: string };
  };
  const { currency } = useContext(CurrencyContext);

  const dispatch = useDispatch();

  const customStyles = cStyler({
    type,
    brand,
    objName: "ferryMSFStyles",
  });

  const search_form = useSelector((state) => state.FerrySearchForm);
  const [isRoundtrip, setIsRoundtrip] = useState({
    value: search_form.isRoundtrip,
  });

  const classes = styles({ customStyles, roundTrip: isRoundtrip.value });

  function onClickPax() {
    setShowPaxPickerModal(true);
  }

  const onClearState = useCallback(() => {
    dispatch(ferrySearchFormReset());
    dispatch(ferrySearchResultsReset());
    dispatch(ferryFiltersReset());
    dispatch(ferrySelectedReset());
  }, [dispatch]);

  function onInitSearch(form) {
    initSearch(form);
  }

  useEffect(() => {
    onClearState();
  }, []);

  async function fetchDestPorts(originPort) {
    const response = await getRoutesByOriginPort(originPort);
    if (
      _.get(response, "routes.length", 0) &&
      response.routes[0].origin_port.code !== _.get(destPortsOptions, "0.origin_port.code")
    ) {
      setDestPortsOptions(response.routes);
    }
  }

  return (
    <div className={classes.FerrySearchForm}>
      <Formik
        initialValues={{
          isRoundtrip: getIsRoundTripOpts(t)[0],
          origin: search_form.origin,
          destination: search_form.destination,
          departDateTime: [search_form.departDateTime],
          returnDateTime: [search_form.returnDateTime],
        }}
        validateOnBlur={false}
        validateOnMount={false}
        validate={(values) => {
          if (values.isRoundtrip.value !== isRoundtrip.value) {
            setIsRoundtrip(values.isRoundtrip);
          }
          if (_.get(values.origin, "origin_meta.0.code", "")) {
            fetchDestPorts(values.origin.origin_meta[0].code);
          }
        }}
        onSubmit={async (values) => {
          if (!isSubmitting) {
            setIsSubmitting(true);
            const destObj = destPortsOptions.find(
              (option) => option.destination_port.code === values.destination.value,
            );
            const hasErrors = validateFerrySearch({
              origin: _.get(values.origin, "origin_meta.0", {}),
              destination: _.get(values.destination, "value", {}),
              departDateTime: values.departDateTime,
              returnDateTime: values.returnDateTime,
              isRoundtrip: values.isRoundtrip.value,
            });
            if (hasErrors) {
              setIsSubmitting(false);
              return;
            }

            const form = {
              origin: values.origin.origin_meta[0],
              destination: destObj.destination_port,
              isRoundtrip: values.isRoundtrip.value,
              departDateTime: values.departDateTime,
              ...(values.isRoundtrip.value ? { returnDateTime: values.returnDateTime[0] } : null),
              currency,
              language: i18n.language,
            };
            await onInitSearch(form);
          }
        }}
      >
        <Form className={classes.form}>
          <div className={classes.formContr}>
            <div className={classes.row1}>
              <span className={classes.mobileToggle}>
                <ReactSelectField
                  name="isRoundtrip"
                  options={getIsRoundTripOpts(t)}
                  customStyles={_.get(customStyles, "inputGroupField", {})}
                />
              </span>
              <PortAutoCompleteField
                id="ferryOriginPort"
                name="origin"
                placeholder={t("selectOriginPort")}
                withErrors={false}
                customStyles={_.get(customStyles, "inputGroupField", {})}
              />
              <ReactSelectField
                id="ferryDestPort"
                name="destination"
                placeholder={t("selectDestPort")}
                options={_.orderBy(destPortsOptions, "name").map((route) => ({
                  value: route.destination_port.code,
                  label: route.destination_port.name_en,
                }))}
                customStyles={_.get(customStyles, "inputGroupField", {})}
              />
            </div>
            <div className={classes.row2}>
              <div />
              <DatepickerField
                name="departDateTime"
                dateFormat="MMM d, yyyy HH:mm"
                minDate={DateTime.now().plus({ days: 1 }).toISODate()}
                maxDate={DateTime.now().plus({ days: 365 }).toISODate()}
                withPortal={true}
                showTimeSelect={true}
                customStyles={_.get(customStyles, "inputGroupField", {})}
              />
              {isRoundtrip.value && (
                <DatepickerField
                  name="returnDateTime"
                  dateFormat="MMM d, yyyy HH:mm"
                  minDate={DateTime.now().plus({ days: 1 }).toISODate()}
                  maxDate={DateTime.now().plus({ days: 365 }).toISODate()}
                  withPortal={true}
                  showTimeSelect={true}
                  customStyles={_.get(customStyles, "inputGroupField", {})}
                />
              )}
              <PaxPicker
                customStyles={_.get(customStyles, "inputGroupField", {})}
                seniors={search_form.seniors}
                adults={search_form.adults}
                childs={search_form.children}
                children_ages={search_form.children_ages}
                onClick={onClickPax}
                product_type="FER"
                showLabel={false}
              />
            </div>
          </div>
          <div className={classes.searchBtnContr}>
            <SearchButton
              id="ferrySearchBtn"
              text={t("search")}
              loading={isSubmitting}
              customStyles={_.get(customStyles, "searchBtn", {})}
            />
          </div>
        </Form>
      </Formik>
    </div>
  );
};
FerrySearchForm.defaultProps = {
  customStyles: {},
};
FerrySearchForm.propTypes = {
  setShowPaxPickerModal: PropTypes.func,
  customStyles: PropTypes.object,
  children: PropTypes.node,
};
